/**
 * FF-AUDIT-20260914-210459-097: a query that ERRORED on its last refetch but
 * still holds data must survive a reload.
 *
 * In a dead zone every `refetchOnMount: 'always'` query ends in
 * `status: 'error'` (UNREACHABLE) with its data intact — that is what keeps
 * the page rendered (-096). The persister, however, dehydrated only
 * `status === 'success'` queries, so the FIRST offline reload restored the
 * record and the SECOND one found nothing: the page fell to "Can't reach the
 * server" with the signed completion still in the outbox. Persisting the
 * error state itself would be worse — `Error` instances serialise to `{}`
 * and a rehydrated `{}` is not an unreachable error, so the page would say
 * "does not exist". So: any query with data is persisted, and an errored one
 * is written back as a stale SUCCESS at its original `dataUpdatedAt`, which
 * is exactly what it was the last time the server answered.
 */
interface PersistedQueryLike {
  state: {
    status: 'pending' | 'error' | 'success'
    data?: unknown
    error?: unknown
    fetchStatus?: string
    dataUpdatedAt?: number
    errorUpdatedAt?: number
    isInvalidated?: boolean
  }
}

export function shouldPersistQuery(state: { status: string; data?: unknown }, root: string, neverPersist: ReadonlySet<string>): boolean {
  return state.data !== undefined && !neverPersist.has(root)
}

/**
 * The state written to disk for one dehydrated query.
 *
 * Every query is written INVALIDATED. A restored query keeps its original
 * `dataUpdatedAt`, so under a `staleTime` (30 s by default) a reload within
 * that window judged the snapshot fresh and never asked the server — a vendor
 * linked in another tab, then a reload, showed the part's hub card, vendor
 * table and history as they were BEFORE the write, with no request in the log
 * (hub-links.spec F31; the same class as the Facilities posture defect that
 * was patched one query at a time with `refetchOnMount: 'always'`). Hydration
 * copies `isInvalidated` onto the restored query, so the snapshot still paints
 * at once (D24 §3) and every query that mounts reconciles with the server —
 * exactly what a reload means. Offline, that refetch settles to UNREACHABLE
 * with the data kept, which is the path any snapshot older than `staleTime`
 * already took.
 */
export function persistableQuery<T extends PersistedQueryLike>(query: T): T {
  if (query.state.status !== 'error' || query.state.data === undefined) {
    return { ...query, state: { ...query.state, isInvalidated: true } }
  }
  return {
    ...query,
    state: { ...query.state, status: 'success', error: null, fetchStatus: 'idle', isInvalidated: true },
  }
}

export function persistableClient<T extends { clientState: { queries: PersistedQueryLike[] } }>(client: T): T {
  return { ...client, clientState: { ...client.clientState, queries: client.clientState.queries.map(persistableQuery) } }
}
